/**
 * Storefront reader for `/site_settings/nav`.
 *
 * EPIC-13 · FEAT-13.4 · STORY-13.4.01.
 *
 * Returns the merged nav (header / footer / announcement). Missing
 * doc → DEFAULT_NAV; permission-denied / other errors → DEFAULT_NAV +
 * source-aware fallback string so storefront callers can render
 * unconditionally.
 *
 * The storefront should consume this via the eventual
 * `useSiteNav()` hook (lands with STORY-13.4.05 storefront
 * integration). For now `getNav()` is the single read entry point
 * for the admin editor + any direct callers.
 */
import { doc, getDoc } from 'firebase/firestore';
import { db } from 'config/firebase';
import logger from 'utils/logger';
import { mergeNav, DEFAULT_NAV } from './siteSettingsNav';

const NAV_DOC = ['site_settings', 'nav'];

/**
 * @returns {Promise<{
 *   nav: ReturnType<typeof mergeNav>,
 *   raw: Object | null,
 *   source: 'firestore' | 'fallback:missing' | 'fallback:denied' | 'fallback:error',
 * }>}
 */
export const getNav = async () => {
  try {
    const snap = await getDoc(doc(db, ...NAV_DOC));
    if (!snap.exists()) {
      return { nav: mergeNav(null), raw: null, source: 'fallback:missing' };
    }
    const data = snap.data() || {};
    return { nav: mergeNav(data), raw: data, source: 'firestore' };
  } catch (err) {
    const code = err?.code || '';
    if (code === 'permission-denied') {
      logger.warn(
        '[siteSettingsNavService] /site_settings/nav — permission denied. '
        + 'Run `firebase deploy --only firestore:rules` to deploy the rules.'
      );
      return { nav: mergeNav(null), raw: null, source: 'fallback:denied' };
    }
    logger.warn('[siteSettingsNavService] getNav read failed:', err);
    return { nav: mergeNav(null), raw: null, source: 'fallback:error' };
  }
};

export { DEFAULT_NAV };

const siteSettingsNavService = { getNav };
export default siteSettingsNavService;
